/* ============================================================
   pj-cart.js — ParkerJoe storefront runtime, layered on Dawn.
   Ported from archive/theme-scratch/assets/parker.js @ 9cd144f.

   What carries forward:
     - getCartTotals(): member discount + tax + free-ship + Parker Points
       estimate — all DISPLAY-ONLY and gated behind reality flags (see below).
       Live audit 2026-06-06 (P0-6): checkout honors none of these yet (no active
       automatic discount, no free-ship rate, no member tiers shipped), so they
       are OFF; the cart advertises only what checkout will actually charge.
     - A small toast helper and reveal-on-scroll observer.
   What was dropped (ADR-002 single Rodeo mode):
     - data-mode switching, mode flash, mode persistence.
   What changed for Dawn:
     - We do NOT intercept add/change; Dawn owns the AJAX cart.
       We SUBSCRIBE to Dawn's `cart-update` pub/sub event and to
       the page-load cart, then render a DISPLAY-ONLY estimate
       into [data-pj-cart-estimate] containers. Shopify checkout
       remains the authoritative source for real totals/tax/ship.
   Config is injected by theme.liquid as window.PJ = { tier, points, loggedIn }
   (loggedIn is consumed by pj-member-discount.js for the Path B code auto-apply).
   ============================================================ */
(function () {
  'use strict';

  var CFG = window.PJ || {};

  /* ---- Reality flags (S3 / P0-6) ------------------------------------------
     Live audit 2026-06-06: checkout honors NO member discount, NO free-shipping
     rate, and no member tiers have shipped. The cart must not advertise any of
     them. Each feature stays code-complete but gated; flip the matching flag ON
     only once the backing config actually exists at checkout:
       - MEMBERSHIP_LIVE     -> the Path A discount Function ships tag-gated
                                automatic discounts (apps/discount-function)
       - FREE_SHIP_THRESHOLD -> a real "free shipping over $X" rate/discount exists
       - LOYALTY_POINTS_LIVE -> Growave Parker Points is configured

     PATH B LIVE 2026-07-06: the member discount IS now honored at checkout via
     segment-gated CIRCLE-* codes auto-applied by pj-member-discount.js
     (MEMBER_CODES_LIVE=true). The real discount (+ Legacy/Heritage free shipping)
     renders NATIVELY in Dawn's cart + checkout, so this display-only estimate
     stays OFF — it would only duplicate the native rows. MEMBERSHIP_LIVE must
     REMAIN false while Path B is live: it belongs to the mutually exclusive
     Path A Function, and enabling both would double-discount members. See
     plan/database/member-codes-path-b.md.

     Re-enabling here (Option A) restores the full estimate verbatim. --------- */
  var MEMBERSHIP_LIVE = false; // MUST stay false while Path B (pj-member-discount.js) is live — mutually exclusive; see plan/database/member-codes-path-b.md
  var FREE_SHIP_THRESHOLD = null; // set to e.g. 150 once a free-ship-over-$X rate is live
  var LOYALTY_POINTS_LIVE = false;

  /* Values applied only when the matching flag above is ON (display estimate
     only — checkout stays authoritative for real totals/tax/shipping). */
  var MEMBER_DISCOUNT = { rancher: 0.15, wrangler: 0.1, rider: 0.05 };
  var FLAT_SHIP = 12;
  var TAX_RATE = 0.0825; // Houston/Harris County estimate; real tax is destination-based
  var POINTS_PER_USD = 2; // Parker Points = round(total * 2)

  var state = {
    tier: (CFG.tier || '').toString().toLowerCase() || null,
    cart: null,
  };

  /* ----------------------------------------------------------
     TOAST
     ---------------------------------------------------------- */
  var toastStack;
  function ensureToastStack() {
    if (!toastStack) {
      toastStack = document.createElement('div');
      toastStack.className = 'pj-toast-stack';
      toastStack.setAttribute('aria-live', 'polite');
      toastStack.setAttribute('aria-atomic', 'true');
      document.body.appendChild(toastStack);
    }
    return toastStack;
  }

  function toast(message) {
    if (!message) return;
    var stack = ensureToastStack();
    var el = document.createElement('div');
    el.className = 'pj-toast';
    el.textContent = message;
    stack.appendChild(el);
    setTimeout(function () {
      el.style.opacity = '0';
      setTimeout(function () {
        if (el.parentNode) el.parentNode.removeChild(el);
      }, 400);
    }, 2400);
  }

  /* ----------------------------------------------------------
     REVEAL ON SCROLL (IntersectionObserver @ 0.12)
     ---------------------------------------------------------- */
  function initReveal(root) {
    root = root || document;
    var els = root.querySelectorAll('.pj-reveal:not(.pj-in)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.classList.add('pj-in');
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('pj-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ----------------------------------------------------------
     CART TOTALS — display-only estimate from /cart.js (cents)
     ---------------------------------------------------------- */
  function getCartTotals() {
    var cart = state.cart;
    var count = (cart && cart.item_count) || 0;

    var subtotalCents = 0;
    if (cart) {
      if (typeof cart.items_subtotal_price === 'number') {
        subtotalCents = cart.items_subtotal_price;
      } else if (Array.isArray(cart.items)) {
        subtotalCents = cart.items.reduce(function (s, i) {
          return s + (i.final_line_price || 0);
        }, 0);
      } else if (typeof cart.total_price === 'number') {
        subtotalCents = cart.total_price;
      }
    }

    var subtotal = subtotalCents / 100;

    // Member discount — only when membership is live AND the shopper has a tier.
    var memberDiscount = MEMBERSHIP_LIVE && state.tier ? MEMBER_DISCOUNT[state.tier] || 0 : 0;
    var discount = subtotal * memberDiscount;
    var afterDiscount = subtotal - discount;

    // Shipping — only estimable when checkout would actually honor a rate:
    // members ship free (when membership is live) or a real free-ship threshold
    // exists. Otherwise shipping is unknown until checkout (carrier-calculated).
    var memberFreeShip = !!(MEMBERSHIP_LIVE && state.tier);
    var freeShipThreshold = typeof FREE_SHIP_THRESHOLD === 'number' ? FREE_SHIP_THRESHOLD : null;
    var shippingKnown = memberFreeShip || freeShipThreshold !== null;
    var shipping = 0;
    if (!memberFreeShip && freeShipThreshold !== null) {
      shipping = afterDiscount >= freeShipThreshold ? 0 : FLAT_SHIP;
    }

    // Tax / points estimates only ride along when we already have an honest
    // shipping figure to total against; otherwise we promise nothing.
    var tax = shippingKnown ? afterDiscount * TAX_RATE : 0;
    var total = afterDiscount + shipping + tax;
    var pointsEarned = LOYALTY_POINTS_LIVE ? Math.round((afterDiscount + shipping) * POINTS_PER_USD) : 0;

    return {
      subtotal: subtotal,
      count: count,
      memberDiscount: memberDiscount,
      discount: discount,
      afterDiscount: afterDiscount,
      memberFreeShip: memberFreeShip,
      freeShipThreshold: freeShipThreshold,
      shippingKnown: shippingKnown,
      shipping: shipping,
      tax: tax,
      total: total,
      pointsEarned: pointsEarned,
      pointsLive: LOYALTY_POINTS_LIVE,
      currency: (cart && cart.currency) || 'USD',
    };
  }

  function formatMoney(amount, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
      }).format(amount);
    } catch (_) {
      return '$' + amount.toFixed(2);
    }
  }

  /* ----------------------------------------------------------
     RENDER ESTIMATE into [data-pj-cart-estimate] containers.
     Re-queried each call because Dawn replaces the cart-drawer
     DOM on update.
     ---------------------------------------------------------- */
  function renderEstimate() {
    var containers = document.querySelectorAll('[data-pj-cart-estimate]');
    if (!containers.length) return;

    var t = getCartTotals();

    containers.forEach(function (box) {
      // Hide when the cart is empty OR when nothing here is honored at checkout.
      // With membership / free-ship / points all OFF, Dawn's native cart already
      // shows the subtotal + "calculated at checkout"; an estimate box would only
      // duplicate it or (worse) advertise a discount/shipping break we can't keep.
      var hasLiveContent =
        t.memberDiscount > 0 ||
        t.memberFreeShip ||
        t.freeShipThreshold !== null ||
        (t.pointsLive && t.pointsEarned > 0);

      if (t.count === 0 || !hasLiveContent) {
        // Path B live: the member's CIRCLE-* code discount renders at CHECKOUT
        // (code discounts don't show on the cart page), so tell the member it's
        // coming — honest, and prevents "my discount is missing" confusion.
        var TIER_NOTE = { rider: ['Heir', '5%', false], wrangler: ['Legacy', '10%', true], rancher: ['Heritage', '15%', true] };
        var note = window.PJ_CODES_LIVE === true && state.tier ? TIER_NOTE[state.tier] : null;
        if (t.count > 0 && note) {
          box.hidden = false;
          box.innerHTML =
            '<p class="pj-cart-estimate__note pj-cart-estimate__note--member">' +
            note[0] + ' member — your ' + note[1] + ' Circle discount' +
            (note[2] ? ' and free shipping' : '') +
            ' will appear at checkout.</p>';
          return;
        }
        box.hidden = true;
        box.innerHTML = '';
        return;
      }
      box.hidden = false;

      var rows = '';

      // Free-shipping messaging — members ship free; otherwise a threshold bar,
      // but only when a real free-ship threshold is actually configured.
      if (t.memberFreeShip) {
        rows +=
          '<div class="pj-cart-ship pj-cart-ship--member">' +
          '<span class="pj-cart-ship__msg">Free shipping included with your membership</span></div>';
      } else if (t.freeShipThreshold !== null) {
        var remaining = t.freeShipThreshold - t.afterDiscount;
        if (remaining <= 0) {
          rows +=
            '<div class="pj-cart-ship pj-cart-ship--unlocked">' +
            '<span class="pj-cart-ship__msg">You’ve unlocked free shipping!</span>' +
            '<span class="pj-cart-ship__bar"><span class="pj-cart-ship__fill" style="width:100%"></span></span></div>';
        } else {
          var pctFill = Math.max(0, Math.min(100, Math.round((t.afterDiscount / t.freeShipThreshold) * 100)));
          rows +=
            '<div class="pj-cart-ship">' +
            '<span class="pj-cart-ship__msg">Add ' +
            formatMoney(remaining, t.currency) +
            ' for free shipping</span>' +
            '<span class="pj-cart-ship__bar"><span class="pj-cart-ship__fill" style="width:' +
            pctFill +
            '%"></span></span></div>';
        }
      }

      if (t.memberDiscount > 0) {
        var pct = Math.round(t.memberDiscount * 100);
        rows +=
          '<div class="pj-cart-estimate__row pj-cart-estimate__row--discount">' +
          '<span>Member discount (' +
          pct +
          '%)</span><span>-' +
          formatMoney(t.discount, t.currency) +
          '</span></div>';
      }

      // Shipping / tax / total rows only when we can total honestly.
      if (t.shippingKnown) {
        rows +=
          '<div class="pj-cart-estimate__row"><span>Est. shipping</span><span>' +
          (t.shipping === 0 ? 'Free' : formatMoney(t.shipping, t.currency)) +
          '</span></div>';
        rows +=
          '<div class="pj-cart-estimate__row"><span>Est. tax</span><span>' +
          formatMoney(t.tax, t.currency) +
          '</span></div>';
        rows +=
          '<div class="pj-cart-estimate__row"><strong>Est. total</strong><strong>' +
          formatMoney(t.total, t.currency) +
          '</strong></div>';
      }

      if (t.pointsLive && t.pointsEarned > 0) {
        rows +=
          '<div class="pj-cart-estimate__points">You’ll earn ≈ ' +
          t.pointsEarned +
          ' Parker Points</div>';
      }

      rows +=
        '<p class="pj-cart-estimate__note">Estimate only — discounts, shipping &amp; tax are finalized at checkout.</p>';

      box.innerHTML = rows;
    });
  }

  /* ----------------------------------------------------------
     CART SYNC — refresh our copy of the cart, then render.
     ---------------------------------------------------------- */
  function refreshCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        state.cart = cart;
        renderEstimate();
        return cart;
      })
      .catch(function () {
        return null;
      });
  }

  /* ----------------------------------------------------------
     INIT — subscribe to Dawn's pub/sub cart events if available.
     ---------------------------------------------------------- */
  function init() {
    initReveal();
    refreshCart();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        // Prefer the cart payload Dawn already fetched; else re-fetch.
        if (event && event.cartData && typeof event.cartData.item_count === 'number') {
          state.cart = event.cartData;
          renderEstimate();
        } else {
          refreshCart();
        }
      });
    }

    // Re-run reveal when Shopify section rerenders in the theme editor.
    document.addEventListener('shopify:section:load', function (e) {
      initReveal(e.target || document);
    });
  }

  /* Public surface (kept small + Dawn-compatible). */
  window.PJStore = {
    getState: function () {
      return state;
    },
    getCartTotals: getCartTotals,
    refreshCart: refreshCart,
    renderEstimate: renderEstimate,
    toast: toast,
    initReveal: initReveal,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
